import { isNil, pickBy } from "lodash";

import {
  customizablePackageCoverageKeys,
  mapNumberToDollarsEnum,
  mapNumberToGolfCartValueEnum,
  mapPrimaryValuesToGraphQL,
  mapStringToPercentEnum,
  type CoverageAccessor,
} from "@covertree/new-business-mappings";

import {
  mapFormKeyToUnderwritingQuestion,
  mapUnderwritingQuestionsFormToQueryInput,
  toUnderwritingQuestionsObject,
  type UnderwritingQuestionsForm,
  type ValueKey,
} from "@covertree/new-business-forms";

import {
  extraCoverageKeys,
  policyLevelExtraCoverageKeys,
  type ExtraCoverageKeys,
} from "@covertree/new-business-graphql";

import type {
  AdditionalInterestInput,
  CreateOrUpdateEndorsementInput,
  EndorsementPolicyLevelCoveragesInput,
  EndorsementUnitChangeInput,
  EndorsementUnitCoveragesInput,
  EndorsementUnitDetailsInput,
  HomeFixtures,
  HomeType,
  RoofShape,
  SkirtingType,
  UnitConstructionInput,
  UnitLocation,
} from "@covertree/agent-portal-graphql";

import { fieldGroupItemKeys, isFieldGroupPeril } from "./fieldGroupPerils";
import type {
  EndorsementFormPayload,
  ProjectedPolicyLevelCoverages,
  ProjectedUWQuestions,
  ProjectedUnitLevelCoverages,
} from "./types";

interface BuildEndorsementInputArgs {
  values: EndorsementFormPayload;
  /** Endorsement being updated; absent opens a new draft on `policyLocator`. */
  endorsementLocator: string | null;
  policyLocator: string | null;
  unitId?: string;
  unitLevelCoverages?: ProjectedUnitLevelCoverages;
  policyLevelCoverages?: ProjectedPolicyLevelCoverages;
  underwritingQuestions?: ProjectedUWQuestions;
  /** Form defaults, i.e. the last saved state; fields equal to it are left out. */
  savedValues?: Partial<EndorsementFormPayload>;
}

const pad = (value: number) => `${value}`.padStart(2, "0");

const toDate = (value: Date | string | null | undefined) => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;

  // AWSDate is a plain calendar date, so take the local Y-M-D rather than
  // `toISOString()`, which would shift the day in negative-offset zones.
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Maps the Home Details form to the backend's construction input.
 *
 * `communityName` and `location` live on `UnitDetails`, so they go through
 * `buildDetails` instead.
 */
const buildConstruction = (
  values: EndorsementFormPayload
): UnitConstructionInput => ({
  homeType: values.homeType as HomeType | undefined,
  manufacturerName: values.manufacturer?.value,
  makeCustom: values.makeCustom,
  modelHome: values.modelHome,
  modelYear: values.modelYear?.value,
  totalSquareFootage: values.totalSquareFootage,
  homeWidth: values.homeWidth,
  homeLength: values.homeLength,
  roofShape: values.roofShape as RoofShape | undefined,
  roofYearYyyy: values.roofYearYyyy,
  homeHudNumber: values.homeHudNumber,
  skirtingType: values.skirtingType as SkirtingType | undefined,
  homeFixtures: values.homeFixtures as HomeFixtures | undefined,
});

const buildDetails = (
  values: EndorsementFormPayload
): EndorsementUnitDetailsInput => ({
  location: values.location as UnitLocation | undefined,
  communityName: values.communityName?.value,
});

type PolicyLevelExtraCoverageKey =
  (typeof policyLevelExtraCoverageKeys)[number];

/** Widened so the unit/policy split below can test membership. */
const POLICY_LEVEL_KEYS: readonly string[] = policyLevelExtraCoverageKeys;

const isUnitExtraCoverageKey = (
  key: ExtraCoverageKeys
): key is Exclude<ExtraCoverageKeys, PolicyLevelExtraCoverageKey> =>
  !POLICY_LEVEL_KEYS.includes(key);

/**
 * Unit-level extra coverages the endorsement may change: the NBS list minus the
 * policy-wide ones. Anything else the Coverage step holds is dropped rather than
 * sent — the form also carries the Insured Info fields.
 *
 * The annotation is the contract check: the build breaks if the NBS list and
 * `EndorsementUnitCoveragesInput` drift apart.
 */
const ENDORSABLE_UNIT_EXTRA_COVERAGE_KEYS: readonly (keyof EndorsementUnitCoveragesInput)[] =
  extraCoverageKeys.filter(isUnitExtraCoverageKey);

/**
 * Policy-wide extra coverages (`policyLevelExtraCoverageKeys`): the key
 * `EndorsementPolicyLevelCoveragesInput` expects -> the form key holding it.
 * Identity fraud is the one coverage the read and write sides spell differently.
 */
const POLICY_FORM_KEY_BY_INPUT_KEY: Record<
  PolicyLevelExtraCoverageKey,
  keyof ProjectedPolicyLevelCoverages
> = {
  identityFraud: "identityFraudExpense",
  scheduledPersonalProperty: "scheduledPersonalProperty",
};

const FORM_VALUE_TO_PERIL_VALUE: Record<
  string,
  (value: string | number, accessor: CoverageAccessor) => string
> = {
  golfCarts: (value) => mapNumberToGolfCartValueEnum(Number(value)),
  earthquake: (value, accessor) =>
    mapStringToPercentEnum(String(value), accessor),
  debrisRemoval: mapNumberToDollarsEnum,
  lossAssessment: mapNumberToDollarsEnum,
  residenceBurglary: mapNumberToDollarsEnum,
  waterBackupAndSumpOverflow: mapNumberToDollarsEnum,
};

/**
 * Maps one form value to what the backend expects.
 */
const toCoverageValue = (key: string, value: unknown) => {
  const toEnum = FORM_VALUE_TO_PERIL_VALUE[key];

  // `null` switches the coverage off and must survive as `null`.
  if (toEnum && (typeof value === "number" || typeof value === "string")) {
    return toEnum(value, `${key}.value` as CoverageAccessor);
  }

  const itemKeys = isFieldGroupPeril(key) ? fieldGroupItemKeys(key) : undefined;

  if (!itemKeys || !Array.isArray(value)) return value;

  return value.map((item) =>
    Object.fromEntries(
      itemKeys
        .map((itemKey) => [itemKey, (item as Record<string, unknown>)[itemKey]])
        // An absent locator must stay absent
        .filter(([, itemValue]) => itemValue != null)
    )
  );
};

type AdditionalInterestFormValue =
  EndorsementFormPayload["additionalInterests"][number];

const toDigits = (value: string) => value.replace(/\D/g, "");

const toAdditionalInterestFields = ({
  type,
  name,
  streetAddress,
  lotUnit,
  city,
  state,
  zipCode,
  accountNumber,
  email,
  isBilling,
}: AdditionalInterestFormValue) => ({
  type,
  name,
  streetAddress,
  lotUnit,
  city,
  state,
  zipCode: toDigits(zipCode),
  accountNumber,
  email,
  isBilling,
});

const isSameAdditionalInterest = (
  a: AdditionalInterestFormValue,
  b: AdditionalInterestFormValue
) =>
  a.type === b.type &&
  a.name === b.name &&
  a.streetAddress === b.streetAddress &&
  a.lotUnit === b.lotUnit &&
  a.city === b.city &&
  a.state === b.state &&
  toDigits(a.zipCode) === toDigits(b.zipCode) &&
  a.accountNumber === b.accountNumber &&
  a.email === b.email &&
  a.isBilling === b.isBilling;

/**
 * Sends the additional interests only when any field changed since the last
 * save; `undefined` leaves them untouched on the backend. Ids are ignored in
 * the comparison and sent only for interests the backend already knows, so a
 * new one is created server-side rather than keyed by a client-generated id.
 */
const buildAdditionalInterests = (
  formInterests: EndorsementFormPayload["additionalInterests"] | undefined,
  savedInterests: EndorsementFormPayload["additionalInterests"] | undefined
): AdditionalInterestInput[] | undefined => {
  if (!formInterests) return undefined;

  const saved = savedInterests ?? [];
  const unmatched = [...saved];
  const unchanged =
    formInterests.length === saved.length &&
    formInterests.every((ai) => {
      const index = unmatched.findIndex((savedAi) =>
        isSameAdditionalInterest(ai, savedAi)
      );

      if (index === -1) return false;

      unmatched.splice(index, 1);

      return true;
    });

  if (unchanged) return undefined;

  const savedIds = new Set(saved.map((ai) => ai.additionalInterestId));

  return formInterests.map((ai) => ({
    ...toAdditionalInterestFields(ai),
    additionalInterestId: savedIds.has(ai.additionalInterestId)
      ? ai.additionalInterestId
      : undefined,
  }));
};

type CoverageFlags = Partial<
  Record<`${ValueKey}Is${"Customizable" | "Hidden"}`, boolean>
>;

/**
 * The field is locked or hidden by the rules. an Insured Info edit can withdraw a coverage the agent already picked, and the rules would reject it.
 */
const isNotSendable = (
  coverage?: CoverageFlags | null,
  field: ValueKey = "value"
) =>
  coverage?.[`${field}IsCustomizable`] === false ||
  coverage?.[`${field}IsHidden`] === true;

/** Base coverages the backend takes as a flat enum, not as `{ value }`. */
const FLAT_BASE_COVERAGE_KEYS: ReadonlySet<string> = new Set([
  "coverageDLossOfUse",
  "coverageEPersonalLiability",
  "coverageEPremisesLiability",
  "coverageFMedicalPaymentToOthers",
  "waterDamage",
]);

/**
 * Maps the base coverage form values (`coverageA_value`, …) with the NBS mapper.
 * Each field is sent only when the rules let the agent change it, so Cov A/B
 * go out as `{ value }`: their settlement option is locked.
 */
const buildBaseCoveragesInput = (
  formCoverages: Record<string, unknown>,
  unitLevelCoverages?: ProjectedUnitLevelCoverages
) => {
  const sendable = pickBy(formCoverages, (value, validationKey) => {
    const [key, field] = validationKey.split("_") as [
      keyof ProjectedUnitLevelCoverages,
      ValueKey,
    ];

    return (
      customizablePackageCoverageKeys.includes(key) &&
      !isNil(value) &&
      !isNotSendable(unitLevelCoverages?.[key], field)
    );
  }) as Parameters<typeof mapPrimaryValuesToGraphQL>[0];

  return Object.fromEntries(
    Object.entries(mapPrimaryValuesToGraphQL(sendable)).map(([key, input]) => [
      key,
      FLAT_BASE_COVERAGE_KEYS.has(key)
        ? (input as { value?: unknown }).value
        : input,
    ])
  );
};

/**
 * Builds the unit-level coverage changes from the Coverage step's form values.
 *
 * The agent's full selection is sent for every coverage the step rendered, not
 * just the changed ones: the backend diffs internally, so re-sending an
 * unchanged selection is a no-op. Coverages the rules hide are left untouched.
 */
export const buildCoveragesInput = (
  values: EndorsementFormPayload,
  unitLevelCoverages?: ProjectedUnitLevelCoverages
): EndorsementUnitCoveragesInput | undefined => {
  const formCoverages = values.units?.[0]?.unitLevelCoverages;

  if (!formCoverages) return undefined;

  const coverages = {
    ...Object.fromEntries(
      ENDORSABLE_UNIT_EXTRA_COVERAGE_KEYS.filter(
        (key) =>
          formCoverages[key] !== undefined &&
          !isNotSendable(unitLevelCoverages?.[key])
      ).map((key) => [key, toCoverageValue(key, formCoverages[key])])
    ),
    ...buildBaseCoveragesInput(formCoverages, unitLevelCoverages),
  };

  return Object.keys(coverages).length > 0 ? coverages : undefined;
};

/**
 * Builds the changes to policy-wide extra coverages to send to BE.
 */
export const buildPolicyCoveragesInput = (
  values: EndorsementFormPayload,
  policyLevelCoverages?: ProjectedPolicyLevelCoverages
): EndorsementPolicyLevelCoveragesInput | undefined => {
  const formCoverages = values.units?.[0]?.unitLevelCoverages;

  if (!formCoverages) return undefined;

  const coverages = Object.fromEntries(
    Object.entries(POLICY_FORM_KEY_BY_INPUT_KEY)
      .filter(
        ([, formKey]) =>
          formCoverages[formKey] !== undefined &&
          !isNotSendable(policyLevelCoverages?.[formKey])
      )
      .map(([inputKey, formKey]) => [
        inputKey,
        toCoverageValue(formKey, formCoverages[formKey]),
      ])
  );

  return Object.keys(coverages).length > 0 ? coverages : undefined;
};

const UNDERWRITING_FORM_KEYS = Object.keys(
  mapFormKeyToUnderwritingQuestion
) as (keyof UnderwritingQuestionsForm)[];

/**
 * Maps the Underwriting section with the NBS mapper.
 * Returns `undefined` if no answer changed since the last save. The backend
 * then keeps the saved answers. `null` and `undefined` both mean "no answer".
 *
 * All answers are mapped, not only the visible ones. The backend keeps only
 * the answers for questions its rules show, and drops the rest.
 */
const buildUnderwritingInput = (
  values: EndorsementFormPayload,
  savedValues: Partial<EndorsementFormPayload> | undefined,
  unitId: string,
  underwritingQuestions: ProjectedUWQuestions | undefined
) => {
  const hasChanged = UNDERWRITING_FORM_KEYS.some(
    (key) => (values[key] ?? undefined) !== (savedValues?.[key] ?? undefined)
  );
  if (!hasChanged) return undefined;

  const { policyLevelUW, unitLevelUW } =
    mapUnderwritingQuestionsFormToQueryInput(
      unitId,
      values,
      false,
      toUnderwritingQuestionsObject(underwritingQuestions ?? [])
    );

  // The agent and new-business enums share their members.
  return {
    policyLevelUnderwritingAnswers:
      policyLevelUW as CreateOrUpdateEndorsementInput["policyLevelUnderwritingAnswers"],
    underwritingAnswers: unitLevelUW[0]
      .answers as EndorsementUnitChangeInput["underwritingAnswers"],
  };
};

/**
 * Builds the `createOrUpdateEndorsement` input from the Insured Info form.
 *
 * The backend accepts only a subset of what the step collects — see
 * `CreateOrUpdateEndorsementInput`.
 *
 * Also carries the Coverage step's extra-coverage selections, which ride on the
 * same `unitChanges` entry.
 */
export const buildEndorsementInput = ({
  values,
  endorsementLocator,
  policyLocator,
  unitId,
  unitLevelCoverages,
  policyLevelCoverages,
  underwritingQuestions,
  savedValues,
}: BuildEndorsementInputArgs): CreateOrUpdateEndorsementInput => {
  const underwriting = unitId
    ? buildUnderwritingInput(values, savedValues, unitId, underwritingQuestions)
    : undefined;

  return {
    // The locator is the discriminator: absent creates a draft on
    // `policyLocator`, present updates the existing one.
    ...(endorsementLocator ? { endorsementLocator } : { policyLocator }),
    effectiveDate: toDate(values.endorsementEffectiveDate),
    reason: values.reason || undefined,
    additionalInterests: buildAdditionalInterests(
      values.additionalInterests,
      savedValues?.additionalInterests
    ),
    // send null to remove
    additionalInsured: values.additionalInsured,
    policyLevelCoverages: buildPolicyCoveragesInput(
      values,
      policyLevelCoverages
    ),
    policyLevelUnderwritingAnswers:
      underwriting?.policyLevelUnderwritingAnswers,
    // `lotUnit` is required whenever a unit change is sent, and the backend writes
    // it onto the address unconditionally. Sending it back is safe because the
    // form seeds it from the policy: unchanged is a no-op, and `""` on a policy
    // that has no lot/unit is also a no-op.
    ...(unitId
      ? {
          unitChanges: [
            {
              unitId,
              endorsement: {
                lotUnit: values.propertyUnitNumber ?? "",
                construction: buildConstruction(values),
                details: buildDetails(values),
              },
              coverages: buildCoveragesInput(values, unitLevelCoverages),
              underwritingAnswers: underwriting?.underwritingAnswers,
            },
          ],
        }
      : {}),
  };
};
